import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Users, TrendingUp, Trophy, FileText, CalendarCheck2, CheckCircle2, Percent, ChevronDown, X } from "lucide-react";
import { useTheme } from "../../hooks/useTheme.js";
import DropDownWithSearch from "../shared/DropDownWIthSearch";
import PlatformCalendar from "../shared_components/platformCalendar";
import "../../css/platformCalendar.css";
import "../../css/CandidateReports.css";
import { calendarOverrideStyles } from "../shared_components/datePickerStyles";
import { fetchRecruiterAnalyticsService, fetchRecruiterAnalyticsDummyService } from "../../Services/recruiterAnalytics.service";

const ALL = "__all__";

const SERIES_COLORS = {
  screened: "#4D70EE",
  scheduled: "#22C55E",
  decided: "#A471F3",
};

const emptySeries = (len) => new Array(len).fill(0);

const KPITile = ({ icon, label, value }) => (
  <div className="w-full dark:bg-gradient-to-r dark:from-[#232142]/10 dark:to-[#7E64A3]/10 bg-bgSecondary rounded-xl flex flex-col">
    <div className="m-4 gap-2 items-center space-y-2">
      <div className="flex items-center space-x-4 font-semibold dark:text-[#AEB9E1] text-[#404040]">
        <span>{icon}</span>
        <span>{label}</span>
      </div>
      <div className="font-bold text-4xl dark:text-[#FFFFFF] text-[#707070]">
        <span>{value}</span>
      </div>
    </div>
  </div>
);

const RecruiterAnalytics = () => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);

  const [selectedRecruiterId, setSelectedRecruiterId] = useState(ALL);
  const [selectedJobId, setSelectedJobId] = useState(ALL);

  const [monthRange, setMonthRange] = useState([null, null]);
  const [fromMonth, toMonth] = monthRange;
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarRef = useRef(null);

  // First/last instant of the selected month, matching how hr_activities is
  // bucketed ("Mon YYYY") — a day-level picker would be misleading here.
  const monthStartISO = (date) => {
    if (!date) return null;
    return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0).toISOString();
  };
  const monthEndISO = (date) => {
    if (!date) return null;
    return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();
  };

  const formatMonthRange = (range) => {
    const opts = { month: "short", year: "numeric" };
    if (!range[0] || !range[1]) return "All Time";
    return `${range[0].toLocaleDateString(undefined, opts)} - ${range[1].toLocaleDateString(undefined, opts)}`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------------- data fetch ---------------- */
  const useDummyData = new URLSearchParams(window.location.search).get("mock") === "1";

  const hasMonthFilter = Boolean(fromMonth && toMonth);
  // A single month picked (start only, no end yet) is a selection in progress —
  // wait for the range to be complete (or fully cleared) before refetching.
  const isPartialSelection = Boolean(fromMonth) !== Boolean(toMonth);

  useEffect(() => {
    if (isPartialSelection) return;

    const load = async () => {
      try {
        setLoading(true);
        const data = useDummyData
          ? await fetchRecruiterAnalyticsDummyService()
          : await fetchRecruiterAnalyticsService(
              hasMonthFilter ? monthStartISO(fromMonth) : null,
              hasMonthFilter ? monthEndISO(toMonth) : null
            );
        setAnalytics(data);
      } catch (err) {
        console.error("Failed to load recruiter analytics", err);
        setError("Could not load recruiter analytics.");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromMonth, toMonth]);

  const months = analytics?.months || [];
  const recruiters = useMemo(() => {
    const list = analytics?.recruiters || [];
    return list.map((r) => {
      const total = (r.screened_total || 0) + (r.scheduled_total || 0) + (r.decided_total || 0);
      return { ...r, total };
    });
  }, [analytics]);

  const grandTotal = useMemo(
    () => recruiters.reduce((sum, r) => sum + r.total, 0),
    [recruiters]
  );

  const sortedLeaderboard = useMemo(
    () => [...recruiters].sort((a, b) => b.total - a.total),
    [recruiters]
  );

  const findRecruiter = (hrId) => recruiters.find((r) => r.hr_id === hrId);
  const findJob = (jobId) => {
    for (const r of recruiters) {
      const job = (r.jobs || []).find((j) => j.job_id === jobId);
      if (job) return { job, owner: r };
    }
    return null;
  };

  const teamMonthly = useMemo(() => {
    const len = months.length;
    return recruiters.reduce(
      (acc, r) => {
        for (let i = 0; i < len; i++) {
          acc.screened[i] += r.screened_monthly?.[i] || 0;
          acc.scheduled[i] += r.scheduled_monthly?.[i] || 0;
          acc.decided[i] += r.decided_monthly?.[i] || 0;
        }
        return acc;
      },
      { screened: emptySeries(len), scheduled: emptySeries(len), decided: emptySeries(len) }
    );
  }, [recruiters, months.length]);

  const scopeData = useMemo(() => {
    if (selectedJobId !== ALL) {
      const found = findJob(selectedJobId);
      if (found) {
        return {
          screened: found.job.screened_monthly,
          scheduled: found.job.scheduled_monthly,
          decided: found.job.decided_monthly,
        };
      }
    }
    if (selectedRecruiterId !== ALL) {
      const r = findRecruiter(selectedRecruiterId);
      if (r) {
        return {
          screened: r.screened_monthly,
          scheduled: r.scheduled_monthly,
          decided: r.decided_monthly,
        };
      }
    }
    return teamMonthly;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecruiterId, selectedJobId, recruiters, teamMonthly]);

  // All-time totals (not scoped to the trailing months window) — feeds the KPI cards.
  const teamTotals = useMemo(
    () =>
      recruiters.reduce(
        (acc, r) => {
          acc.screened += r.screened_total || 0;
          acc.scheduled += r.scheduled_total || 0;
          acc.decided += r.decided_total || 0;
          return acc;
        },
        { screened: 0, scheduled: 0, decided: 0 }
      ),
    [recruiters]
  );

  const scopeTotals = useMemo(() => {
    if (selectedJobId !== ALL) {
      const found = findJob(selectedJobId);
      if (found) {
        return {
          screened: found.job.screened_total || 0,
          scheduled: found.job.scheduled_total || 0,
          decided: found.job.decided_total || 0,
        };
      }
    }
    if (selectedRecruiterId !== ALL) {
      const r = findRecruiter(selectedRecruiterId);
      if (r) {
        return {
          screened: r.screened_total || 0,
          scheduled: r.scheduled_total || 0,
          decided: r.decided_total || 0,
        };
      }
    }
    return teamTotals;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecruiterId, selectedJobId, recruiters, teamTotals]);

  const totalConversionPct = scopeTotals.scheduled
    ? (scopeTotals.decided / scopeTotals.scheduled) * 100
    : 0;

  const trendChartData = months.map((m, i) => ({
    name: m,
    screened: scopeData.screened?.[i] || 0,
    scheduled: scopeData.scheduled?.[i] || 0,
    decided: scopeData.decided?.[i] || 0,
  }));

  const recruiterOptions = [
    { label: "All Recruiters", value: ALL },
    ...recruiters.map((r) => ({ label: r.hr_name, value: r.hr_id })),
  ];

  const jobOptions = useMemo(() => {
    const base = [{ label: "All Jobs", value: ALL }];
    if (selectedRecruiterId === ALL) {
      recruiters.forEach((r) => {
        (r.jobs || []).forEach((j) => {
          base.push({ label: j.job_role, value: j.job_id });
        });
      });
    } else {
      const r = findRecruiter(selectedRecruiterId);
      (r?.jobs || []).forEach((j) => base.push({ label: j.job_role, value: j.job_id }));
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecruiterId, recruiters]);

  // DropDownWithSearch keeps its input focused after a click-selection (by
  // design, so the click isn't lost to a blur race) — drop focus afterwards
  // so the input doesn't sit there looking active once a value is picked.
  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const handleRecruiterSelect = (item) => {
    setSelectedRecruiterId(item.value || ALL);
    setSelectedJobId(ALL);
    blurActiveElement();
  };

  const handleJobSelect = (item) => {
    const jobId = item.value || ALL;
    if (jobId !== ALL) {
      const found = findJob(jobId);
      if (found && found.owner.hr_id !== selectedRecruiterId) {
        setSelectedRecruiterId(found.owner.hr_id);
      }
    }
    setSelectedJobId(jobId);
    blurActiveElement();
  };

  const applyRecruiterFilter = (hrId) => {
    setSelectedRecruiterId(hrId);
    setSelectedJobId(ALL);
  };

  const recruiterLabel =
    selectedRecruiterId === ALL ? "All Recruiters" : findRecruiter(selectedRecruiterId)?.hr_name || "—";
  const jobLabel = selectedJobId === ALL ? "All Jobs" : findJob(selectedJobId)?.job.job_role || "—";

  const fmt = (n) => (n ?? 0).toLocaleString("en-IN");

  return (
    <div className="min-h-screen flex flex-col w-full p-4 gap-2">
      <style>{calendarOverrideStyles(theme)}</style>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 justify-end">
        <div className="w-64">
          <DropDownWithSearch
            dropdownData={recruiterOptions}
            onSelect={handleRecruiterSelect}
            placeholder="All Recruiters"
            selectedValue={recruiterOptions.find((o) => o.value === selectedRecruiterId) || null}
            showClearButton={false}
          />
        </div>
        <div className="w-64">
          <DropDownWithSearch
            dropdownData={jobOptions}
            onSelect={handleJobSelect}
            placeholder="All Jobs"
            selectedValue={jobOptions.find((o) => o.value === selectedJobId) || null}
            showClearButton={false}
          />
        </div>
        <div className="relative" ref={calendarRef}>
          <button
            type="button"
            className="flex items-center justify-between w-48 px-4 py-2 pr-4 border border-gray-300 rounded-[30px] bg-bgPrimary text-textPrimary hover:border-gray-400 focus:border-gray-400 focus:outline-none dark:glass-effect dark:bg-[linear-gradient(233.57deg,rgba(34,52,97,0.25)_-5.01%,rgba(11,10,31,0.05)_100%)] dark:border-[rgba(229,229,229,0.5)] dark:hover:border-white dark:focus:border-white text-sm"
            onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            aria-expanded={isCalendarOpen}
          >
            <span
              className={
                fromMonth || toMonth
                  ? "text-textPrimary dark:text-[rgba(216,216,216,1)]"
                  : "text-gray-400 dark:text-[rgba(216,216,216,0.6)]"
              }
              style={{ maxWidth: "160px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {formatMonthRange(monthRange)}
            </span>
            {fromMonth || toMonth ? (
              <X
                className="w-4 h-4 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-white flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setMonthRange([null, null]);
                  setIsCalendarOpen(false);
                }}
              />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-300 flex-shrink-0" />
            )}
          </button>
          {isCalendarOpen && (
            <div
              className="candidate-analytics-calendar absolute mt-2 z-10 right-0"
              style={{
                background: `${theme === "dark" ? "#0a1733" : ""}`,
                backdropFilter: "blur(500px)",
                backgroundColor: `${theme === "light" && "rgb(255 255 255)"}`,
              }}
            >
              <div className={`platform-calendar ${theme}`}>
                <PlatformCalendar
                  pickerMode="month"
                  onChange={(value) => {
                    setMonthRange(value);
                    if (value[0] && value[1]) setIsCalendarOpen(false);
                  }}
                  value={monthRange}
                  selectRange={true}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="relative w-full p-6 dark:bg-gradient-to-r from-[#232142]/10 to-[#7E64A3]/10 bg-bgSecondary rounded-xl flex items-center justify-center">
          <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">{error}</span>
        </div>
      )}

        {loading && !error && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-24 rounded-xl animate-pulse bg-gray-200 dark:bg-gray-900" />
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-4 items-stretch">
              <div className="h-72 rounded-xl animate-pulse bg-gray-200 dark:bg-gray-900" />
              <div className="h-full min-h-[320px] rounded-xl animate-pulse bg-gray-200 dark:bg-gray-900" />
            </div>
          </div>
        )}

        {!loading && !error && recruiters.length === 0 && (
          <div className="relative w-full p-10 dark:bg-gradient-to-r from-[#232142]/10 to-[#7E64A3]/10 bg-bgSecondary rounded-xl flex items-center justify-center">
            <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">
              No recruiter activity recorded yet.
            </span>
          </div>
        )}

        {!loading && !error && recruiters.length > 0 && (
          <div className="flex flex-col gap-4">
            {/* KPI strip, full width */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <KPITile icon={<FileText className="w-5 h-5" />} label="Resumes Screened" value={fmt(scopeTotals.screened)} />
              <KPITile icon={<CalendarCheck2 className="w-5 h-5" />} label="Interviews Scheduled" value={fmt(scopeTotals.scheduled)} />
              <KPITile icon={<CheckCircle2 className="w-5 h-5" />} label="Decisions Taken" value={fmt(scopeTotals.decided)} />
              <KPITile icon={<Percent className="w-5 h-5" />} label="Interview → Decision" value={`${totalConversionPct.toFixed(1)}%`} />
            </div>

            {/* Trend chart + leaderboard, side by side for direct comparison */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-4 items-start">
              <div className="h-[424px] dark:bg-gradient-to-r dark:from-[#232142]/10 dark:to-[#7E64A3]/10 bg-bgSecondary rounded-xl p-6 flex flex-col">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-[#404040] dark:text-[#AEB9E1]" />
                    <h2 className="text-[#404040] dark:text-[#AEB9E1] text-lg font-medium tracking-wide">
                      {months.length}-Month Trend
                      <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                        — {recruiterLabel}
                        {selectedJobId !== ALL ? ` · ${jobLabel}` : ""}
                      </span>
                    </h2>
                  </div>
                </div>
                <div className="flex-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="4 4" stroke={isDark ? "#34353CA3" : "#E0E0E0"} />
                      <XAxis dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis
                        tick={{ fill: "#9CA3AF", fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        tickCount={6}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: isDark ? "#1a1a19" : "#ffffff",
                          borderRadius: "8px",
                          border: "none",
                          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                        }}
                        itemStyle={{ fontSize: "12px" }}
                        labelStyle={{ color: "#9CA3AF" }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 12, color: isDark ? "#AEB9E1" : "#707070" }}
                        formatter={(value) =>
                          value === "screened" ? "Resumes Screened" : value === "scheduled" ? "Interviews Scheduled" : "Decisions Taken"
                        }
                      />
                      <Line type="monotone" dataKey="screened" stroke={SERIES_COLORS.screened} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="scheduled" stroke={SERIES_COLORS.scheduled} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="decided" stroke={SERIES_COLORS.decided} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Leaderboard, narrow column beside the trend chart */}
              <div className="h-[424px] dark:bg-gradient-to-r dark:from-[#232142]/10 dark:to-[#7E64A3]/10 bg-bgSecondary rounded-xl p-6 flex flex-col">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-5 h-5 text-[#404040] dark:text-[#AEB9E1]" />
                    <h2 className="text-[#404040] dark:text-[#AEB9E1] text-lg font-medium tracking-wide">
                      Leaderboard
                    </h2>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    All time · share of total
                  </span>
                </div>

                <div className="flex-1 flex flex-col gap-1 overflow-y-auto">
                  {sortedLeaderboard.map((r, rank) => {
                    const pct = grandTotal ? (r.total / grandTotal) * 100 : 0;
                    const isCurrent = r.hr_id === selectedRecruiterId;
                    return (
                      <button
                        key={r.hr_id}
                        type="button"
                        onClick={() => applyRecruiterFilter(r.hr_id)}
                        className={`flex flex-col gap-1.5 rounded-lg px-2 py-2.5 text-left transition-colors min-h-[80px] flex-shrink-0 ${
                          isCurrent
                            ? "bg-[#4071FF]/10"
                            : "hover:bg-black/5 dark:hover:bg-white/5"
                        }`}
                      >
                        <span className="flex items-center justify-between gap-2 min-w-0">
                          <span
                            className={`flex items-center gap-1.5 text-sm font-semibold truncate ${
                              isCurrent ? "text-[#4071FF] dark:text-[#8CA6FF]" : "text-gray-800 dark:text-white"
                            }`}
                          >
                            <Users className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />
                            <span className="truncate">{r.hr_name}</span>
                            {rank === 0 && (
                              <span className="text-[9px] font-bold uppercase tracking-wide text-[#4071FF] dark:text-[#8CA6FF] bg-[#4071FF]/10 rounded px-1 py-0.5 flex-shrink-0">
                                Top
                              </span>
                            )}
                          </span>
                          <span className="text-xs font-medium text-gray-800 dark:text-white flex-shrink-0">
                            {pct.toFixed(1)}%
                          </span>
                        </span>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                          {fmt(r.total)} actions
                        </span>
                        <span className="h-1 rounded bg-black/5 dark:bg-white/10 overflow-hidden">
                          <span
                            className="block h-full rounded bg-gradient-to-r from-pink-500 to-blue-500"
                            style={{ width: `${pct.toFixed(1)}%` }}
                          />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default RecruiterAnalytics;
